import { v4 as uuid } from 'uuid';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	START_NODE_ID,
	transitionType,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import {
	ACTION_RULE_TYPE,
	CONDITION_RULE_TYPE,
	type ConditionGroup,
	type MetaWorkflowDocumentWithUsages,
	type Rule,
	SCREEN_RULE_TYPE,
	TRIGGER_RULE_TYPE,
	VALIDATOR_RULE_TYPE,
	type Transition,
	type ApiAssociation,
	type ApiConditionGroup,
	type ApiGetWorkflowResponseWithUsages,
	type ApiRule,
	type ApiTransition,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { transformWithUsages } from './transformWithUsages.tsx';
import { validateLogicalDocumentIntegrity } from './WorkflowIntegrityError.tsx';

type IssueTypeId = string;

type StatusId = string;

const flattenConditionsRecursively = (conditionGroup?: ApiConditionGroup): ApiRule[] => {
	if (!conditionGroup) {
		return [];
	}

	return conditionGroup.conditions.concat(
		conditionGroup.conditionGroups.flatMap(flattenConditionsRecursively),
	);
};

const transformConditionGroupRecursively = (conditionGroup: ApiConditionGroup): ConditionGroup => ({
	id: uuid(),
	operation: conditionGroup.operation,
	conditionGroups: conditionGroup.conditionGroups.map(transformConditionGroupRecursively),
	conditionIds: conditionGroup.conditions.map(({ id }) => id),
});

const transformRule = (rule: ApiRule): Rule => ({
	id: rule.id,
	displayText: '',
	ruleKey: rule.ruleKey,
	config: rule.parameters,
	...(rule.type ? { type: rule.type } : {}),
});

const transformRules = (transition: ApiTransition) => {
	const actions = (transition.actions || []).map((action) => ({
		...action,
		type: ACTION_RULE_TYPE,
	}));
	const conditions = flattenConditionsRecursively(transition.conditions).map((condition) => ({
		...condition,
		type: CONDITION_RULE_TYPE,
	}));
	const validators = (transition.validators || []).map((validator) => ({
		...validator,
		type: VALIDATOR_RULE_TYPE,
	}));
	const triggers = (transition.triggers || []).map((trigger) => ({
		...trigger,
		type: TRIGGER_RULE_TYPE,
	}));
	const screens = transition.transitionScreen
		? [{ ...transition.transitionScreen, type: SCREEN_RULE_TYPE }]
		: [];

	const rules = [...actions, ...conditions, ...validators, ...triggers, ...screens];

	let conditionTree: ConditionGroup | undefined;

	if (transition.conditions) {
		conditionTree = transformConditionGroupRecursively(transition.conditions);
	}

	return {
		rules: rules.map(transformRule),
		conditionTree,
	};
};

const getTransitionLinks = (transition: ApiTransition): Transition['links'] => {
	const { links } = transition;

	if (transition.type === transitionType.INITIAL) {
		return [
			{
				fromRef: START_NODE_ID,
				fromPort: 0,
				toPort: links[0]?.toPort || 0,
			},
		];
	}

	if (transition.type === transitionType.GLOBAL) {
		return [];
	}

	return links.reduce<Transition['links']>((acc, link) => {
		if (link.fromStatusReference !== undefined) {
			acc.push({
				fromRef: link.fromStatusReference,
				fromPort: link.fromPort,
				toPort: link.toPort,
			});
		}
		return acc;
	}, []);
};

const transformTransitions = (transitions: ApiTransition[]) => {
	const transformed: Record<string, Transition> = {};

	transitions.forEach((transition) => {
		transformed[transition.id] = {
			id: transition.id,
			type: transitionType[transition.type],
			name: transition.name,
			description: transition.description || '',
			toRef: transition.toStatusReference,
			links: getTransitionLinks(transition),
			properties: transition.properties ?? {},
			...transformRules(transition),
			...(transition.customIssueEventId
				? { customIssueEventId: transition.customIssueEventId }
				: {}),
		};
	});
	return transformed;
};

export const transform = (
	response: ApiGetWorkflowResponseWithUsages,
): MetaWorkflowDocumentWithUsages => {
	const [workflow] = response.workflows;

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const allStatuses: Record<string, any> = {};
	const visibleStatuses: Array<{
		ref: StatusId;
	}> = [];

	const getAssociatedIssueTypes = (associations?: ApiAssociation[]): IssueTypeId[] =>
		associations?.length ? associations[0].issueTypes.map((id) => id) : [];

	response.statuses.forEach((status) => {
		const workflowStatus = workflow.statuses.find(
			(candidate) => candidate.statusReference === status.id,
		);

		allStatuses[status.statusReference] = {
			id: status.id,
			name: status.name,
			description: status.description,
			isNew: false,
			statusCategory: status.statusCategory,
			scope: status.scope.type,
			layout:
				workflowStatus?.layout && workflowStatus.layout.x && workflowStatus.layout.y
					? workflowStatus.layout
					: undefined,
			properties: workflowStatus?.properties ?? {},
			...(workflowStatus?.approvalConfiguration && {
				approvalConfiguration: workflowStatus.approvalConfiguration,
			}),
		};
	});

	workflow.statuses.forEach((workflowStatus) => {
		if (allStatuses[workflowStatus.statusReference]) {
			visibleStatuses.push({ ref: workflowStatus.statusReference });
		}
	});

	return {
		id: workflow.id,
		name: workflow.name,
		description: workflow.description,
		versionNumber: workflow.version.versionNumber,
		versionId: workflow.version.id,
		scope: workflow.scope,
		allStatuses,
		visibleStatuses,
		transitions: transformTransitions(workflow.transitions),
		startNodeLayout: workflow.startPointLayout || {},
		loopedTransitionContainerLayout: workflow.loopedTransitionContainerLayout || {},
		associatedIssueTypeIds: getAssociatedIssueTypes(workflow.associatedWith),
		associatedEntityIds: workflow.associatedWith.map(({ project, issueTypes }) => ({
			project: { id: project.id },
			issueTypes: issueTypes.map((id) => id),
		})),
		migrationId: workflow.migrationId,
		isEditable: workflow.isEditable,
		hasDraft: workflow.hasDraft,
	};
};

export function transformAPIToMetaDocument(
	apiResponse: ApiGetWorkflowResponseWithUsages,
): MetaWorkflowDocumentWithUsages {
	try {
		validateLogicalDocumentIntegrity(apiResponse);
	} catch (err: unknown) {
		// an error here will not block the user experience, but it triggers
		// a priority alarm for us to investigate and understand the occurrence.
		log.safeErrorWithoutCustomerData(
			'workflow.render.integrity.checker',
			err instanceof Error ? err.message : String(err),
		);
	}
	return transformWithUsages(apiResponse);
}
